import React, { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { useInView } from 'react-intersection-observer';
import Heading from 'components/shared/heading';
import Button from 'components/shared/button';
import Link from 'components/shared/link';
import styles from './styles.module.scss';

const WistiaPlayerLazy = lazy(() => import('components/shared/wistia-player'));

// Gatsby image markup differs between SSR and client; mark the subtree so React skips diffing.
const withSuppressHydrationWarning = (node) => {
  if (!React.isValidElement(node)) return node;
  const children = React.Children.map(node.props.children, withSuppressHydrationWarning);
  return React.cloneElement(node, { suppressHydrationWarning: true }, children);
};

const ArrowRight = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path
      d="M3 8h10M9 4l4 4-4 4"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ShieldCheck = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path
      d="M8 1.5L2 4v4c0 3.3 2.5 5.8 6 6.5 3.5-.7 6-3.2 6-6.5V4L8 1.5z"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinejoin="round"
    />
    <path
      d="M5.5 8l2 2 3-3"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const HeroSplit = ({
  eyebrow = '',
  eyebrowTag = 'span',
  title = null,
  titleTag = 'h1',
  description = '',
  buttonLabel = '',
  buttonLink = '',
  secondaryButtonLabel = '',
  secondaryButtonLink = '',
  trustText = '',
  video = '',
  imageFit = 'cover',
  textWidth = '50',
  theme = '',
  children = null,
}) => {
  const [isClient, setIsClient] = useState(false);
  const wistiaPlayerRef = useRef(null);
  const [mediaRef, inView] = useInView({ threshold: 0.4 });

  useEffect(() => {
    setIsClient(true);
  }, []);

  // The image (if any) is passed as the block's inner content; take the first node.
  const imageNode = useMemo(() => {
    const first = React.Children.toArray(children).filter((c) => React.isValidElement(c))[0];
    return first ? withSuppressHydrationWarning(first) : null;
  }, [children]);

  const hasMedia = Boolean(video) || Boolean(imageNode);

  const placeStyleToWistia = useCallback((player) => {
    const shadowRoot = player?.shadowRoot;
    if (!shadowRoot) return;
    const styleEl = document.createElement('style');
    styleEl.textContent = `
      video { width: 100% !important; height: 100% !important; object-fit: cover !important; }
      .click-for-sound-btn { display: none !important; }
    `;
    shadowRoot.appendChild(styleEl);
  }, []);

  const handleApiReady = useCallback(
    (event) => {
      const player = event?.target;
      if (!player) return;
      wistiaPlayerRef.current = player;
      placeStyleToWistia(player);
    },
    [placeStyleToWistia]
  );

  // Play only while in view (decorative, muted, looping).
  useEffect(() => {
    const player = wistiaPlayerRef.current;
    if (!player) return undefined;
    if (inView) player.play?.();
    else player.pause?.();
    return () => player.pause?.();
  }, [inView]);

  if (!title) {
    return null;
  }

  const EyebrowTag = eyebrowTag === 'h1' ? 'h1' : 'span';
  const hasCtas = (buttonLabel && buttonLink) || (secondaryButtonLabel && secondaryButtonLink);
  // WP delivers numeric select values as numbers (the lazy-blocks parser JSON-parses
  // them), so normalise textWidth to a string before matching the modifier class.
  const textWidthKey = String(textWidth);

  return (
    <section className={cn(styles.root, { [styles.themeDark]: theme === 'dark' })}>
      <div className="container">
        <div
          className={cn(styles.inner, {
            [styles.noMedia]: !hasMedia,
            [styles.text45]: hasMedia && textWidthKey === '45',
            [styles.text40]: hasMedia && textWidthKey === '40',
            [styles.text35]: hasMedia && textWidthKey === '35',
          })}
        >
          <div className={styles.lead}>
            {eyebrow && <EyebrowTag className={styles.eyebrow}>{eyebrow}</EyebrowTag>}
            <Heading
              tag={titleTag}
              size="xxl"
              className={styles.title}
              innerHTML={title}
              highlightedWordsWithoutWrap={false}
            />
          </div>

          {hasMedia && (
            <div
              className={cn(styles.media, {
                [styles.fitArea]: imageFit === 'fit',
              })}
              ref={mediaRef}
            >
              {isClient && video ? (
                <Suspense fallback={null}>
                  <WistiaPlayerLazy
                    mediaId={video}
                    ref={wistiaPlayerRef}
                    onApiReady={handleApiReady}
                    autoPlay
                    playlistLoop
                    muted
                    className={styles.wistiaPlayer}
                    poster={false}
                    swatch={false}
                    controls={false}
                    resumable
                  />
                </Suspense>
              ) : (
                imageNode
              )}
            </div>
          )}

          <div className={styles.body}>
            {description && (
              <div
                className={styles.description}
                dangerouslySetInnerHTML={{ __html: description }}
              />
            )}

            {hasCtas && (
              <div className={styles.ctas}>
                {buttonLabel && buttonLink && (
                  <Button to={buttonLink} theme="accent-primary" className={styles.primaryBtn}>
                    {buttonLabel}
                    <ArrowRight />
                  </Button>
                )}
                {secondaryButtonLabel && secondaryButtonLink && (
                  <Link to={secondaryButtonLink} className={styles.secondaryBtn}>
                    {secondaryButtonLabel}
                    <ArrowRight />
                  </Link>
                )}
              </div>
            )}

            {trustText && (
              <div className={styles.trust}>
                <ShieldCheck />
                <span>{trustText}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

HeroSplit.propTypes = {
  eyebrow: PropTypes.string,
  eyebrowTag: PropTypes.oneOf(['span', 'h1']),
  title: PropTypes.string, // HTML; wrap words in <span> to color them
  titleTag: PropTypes.oneOf(['h1', 'h2']),
  description: PropTypes.string,
  buttonLabel: PropTypes.string,
  buttonLink: PropTypes.string,
  secondaryButtonLabel: PropTypes.string,
  secondaryButtonLink: PropTypes.string,
  trustText: PropTypes.string,
  video: PropTypes.string, // Wistia mediaId
  // How the image fits the media area (images only — video is always cover):
  // 'cover' = fill + crop (default), 'fit' = the area sizes to the image's natural
  // ratio (whole image, no crop). Both round the image itself.
  imageFit: PropTypes.oneOf(['cover', 'fit']),
  // Text (left) column width on desktop, as a %: 50 (default), 45, 40, 35. WP delivers it
  // as a number; a string works too.
  textWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  theme: PropTypes.oneOf(['', 'dark']),
  children: PropTypes.node, // one image for the media area
};

export default HeroSplit;
